import {Component, AfterViewInit, OnInit, OnDestroy} from '@angular/core';
import {NavigationEnd, NavigationStart, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import { AuthTokenExpiredUIMessage } from '../../../shared-components/ui-messages/auth-token-expired-ui-message.interface';
import { MessageBusService } from '../../../shared-components/message-bus.service';
import { Logger } from '../../../shared-components/logging/logger.interface';
import { SessionService } from '../../../shared-components/authentication/session-service.service';
import { TelemetryService } from 'projects/shared-components/telemetry/telemetry.service';
import { LoggerService } from 'projects/shared-components/logging/logger-factory.service';
import {DateTime} from "luxon";
import {LocationService} from "../../../shared-components/location.service";
import {MaintenanceScreenService} from "../../../shared-components/maintenance-screen/maintenance-screen.service";
import {DismissToken} from "../../../shared-components/authentication/operations/dismiss-token.class";
import {AuthServiceClientService} from "../../../shared-components/authentication/auth-service-client.service";
import {LogoutReasonService} from "../../../shared-components/logout-reason.service";
import { EssConnectionDiagnosticsService } from '../../../shared-components/event-stream/ess-connection-diagnostics.service';

@Component({
   selector: 'ets-root',
   templateUrl: './wt-app.component.html',
   styleUrls: ['./wt-app.component.scss']
})
export class WebtraderAppComponent implements OnInit, AfterViewInit, OnDestroy {
   private readonly _logger: Logger;

   constructor(
      private readonly _router: Router,
      private readonly _messageBus: MessageBusService,
      private readonly _sessionService: SessionService,
      private readonly _telemetryService: TelemetryService,
      private readonly _maintenanceScreen: MaintenanceScreenService,
      private readonly _authService: AuthServiceClientService,
      private readonly _logoutReason: LogoutReasonService,
      loggerService: LoggerService,
      private readonly _essDiagnostics: EssConnectionDiagnosticsService
   ) {
      this._logger = loggerService.createLogger('AppComponent');
   }

   private _interval: number;

   ngOnInit() {
      this._essDiagnostics.start();
      this._interval = setInterval(() => this.maintenanceWatcher(), 1000) as any;

      // CR-20260508-001 iter 22 — authoritative overlay-clear on
      // navigation. Hash-based hashchange listener in
      // assets/session-sync-overlay.js cannot see Angular Router
      // navigations made with `skipLocationChange: true` (used by the
      // logout flow at line 70 and the maintenance redirect at line 91)
      // because they don't update window.location.hash. Subscribe to
      // Router.events directly — fires for every navigation, programmatic
      // or user-driven, regardless of skipLocationChange.
      this._router.events
         .pipe(filter(e => e instanceof NavigationEnd))
         .subscribe((e: NavigationEnd) => {
            const url = e.urlAfterRedirects || e.url || '';
            const onApp = url.indexOf('/app') === 0 || url.indexOf('app') === 0;
            // CR-20260508-001 iter 23 — publish authoritative route status
            // for the vanilla overlay's visibilitychange gate. The kickout
            // flow navigates to /logout with skipLocationChange:true, so
            // window.location.hash stays at #/app and the hash-based gate
            // wrongly re-shows the overlay on next tab-return after a
            // logout-while-hidden. Router state is the truth here.
            (window as any).__etsRouteIsApp = onApp;
            if (!onApp) {
               const hook = (window as any).__etsSessionSyncOverlay;
               if (hook && typeof hook.hide === 'function') {
                  hook.hide();
               }
            }
         });
   }

   ngOnDestroy() {
      clearInterval(this._interval);
   }

   async ngAfterViewInit(): Promise<void> {
      this._telemetryService.init();

      this._messageBus
         .of<AuthTokenExpiredUIMessage>('AuthTokenExpiredUIMessage')
         .subscribe(data => this.onAuthTokenChanged(data.payload));

      await this._router.navigate(['/login']);
   }


   private async onAuthTokenChanged(message: AuthTokenExpiredUIMessage) {
      this._logger.info('Received message that auth. token is expired', message);
      // CR-20260424-003 — central reason-forwarding for the logout screen.
      // Any publisher (component button, ess-worker UI-lag detector, server
      // LogoutMessage) sets a contextual message via this single hop.
      if (message.source) {
         this._logoutReason.setReason(message.source);
      }
      this._sessionService.reset();
      this._logger.info('User session has been reset');
      const targetRoute = message.doNotShowLogoutScreen ? '/login' : '/logout';
      await this._router.navigate([targetRoute], { skipLocationChange: targetRoute === '/logout' });
   }

   private maintenanceWatcher() {
      if (this._router.url.includes('/maintenance')) {
         return;
      }

      if (!this._maintenanceScreen.isMaintenanceTime()) {
         return;
      }

      const cmd = new DismissToken(this._sessionService.authToken, 'maintenance');
      this._authService
          .processCommand(cmd)
          .then(() => {
          })
          .catch(e =>
              console.debug('session dismiss operation failed during maintenance', { error: e })
          );

      this._router.navigate(['/logout'], { skipLocationChange: true })
          .then(x => {});
   }
}
